import { memo, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import "./AddEmployeeModal.css";
import { firebaseUploadEmployeeImage } from "../../services/firebaseUpload";
import { serviceEmployeePostAdd } from "../../api/serviceFleetMaster";

const initialEmployeeState = {
  vehicleId: null,
  name: "",
  email: "",
  phone: "",
  passcode: "",
};

const selectStyles = {
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 9999,
  }),
};

const AddEmployeeModal = ({
  onClose,
  onEmployeeAdded,
  vehicleOptions = [],
}) => {
  const fileInputRef = useRef(null);

  const [image, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const [updating, setUpdating] = useState(false);

  const [newEmployee, setNewEmployee] = useState(initialEmployeeState);

  /*
   * Create a temporary image-preview URL and clean it up
   * when the selected image changes or the modal unmounts.
   */
  useEffect(() => {
    if (!image) {
      setImagePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(image);

    setImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [image]);

  const selectedVehicleOption = useMemo(() => {
    if (!newEmployee.vehicleId) {
      return null;
    }

    return (
      vehicleOptions.find(
        (option) => String(option.value) === String(newEmployee.vehicleId),
      ) || null
    );
  }, [vehicleOptions, newEmployee.vehicleId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setNewEmployee((currentEmployee) => ({
      ...currentEmployee,
      [name]: value,
    }));
  };

  const handleVehicleChange = (option) => {
    setNewEmployee((currentEmployee) => ({
      ...currentEmployee,
      vehicleId: option?.value ?? null,
    }));
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");

      event.target.value = "";
      return;
    }

    setImage(selectedFile);
  };

  const openImagePicker = () => {
    if (updating) {
      return;
    }

    fileInputRef.current?.click();
  };

  const validateEmployee = () => {
    if (!newEmployee.name.trim()) {
      throw new Error("Employee name is required.");
    }

    if (!newEmployee.email.trim()) {
      throw new Error("Email address is required.");
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmployee.email.trim())) {
      throw new Error("Enter a valid email address.");
    }
  };

  const serverAddEmployee = async () => {
    try {
      validateEmployee();
      setUpdating(true);

      /*
       * Only call the upload service when an image has
       * actually been selected.
       */
      const imageUrl = null;
      // const imageUrl = image
      //   ? await firebaseUploadEmployeeImage({
      //       organizationId,
      //       file: image,
      //     })
      //   : null;

      const payload = {
        vehicleId: newEmployee.vehicleId || null,

        name: newEmployee.name.trim(),
        email: newEmployee.email.trim().toLowerCase(),
        phone: newEmployee.phone.trim() || null,
        passcode: newEmployee.passcode.trim() || null,

        imageUrl: imageUrl || null,
      };

      if (import.meta.env.DEV) {
        console.log("Add employee payload:", payload);
      }

      await serviceEmployeePostAdd(payload);

      toast.success("Employee added successfully!");

      await onEmployeeAdded?.();
      onClose();
    } catch (error) {
      console.error("Add employee error:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Error: Contact Trucktronix!";

      if (errorMessage.includes("409")) {
        toast.error("Email already exists.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await serverAddEmployee();
  };

  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  return (
    <div className="aem-shell">
      <button
        type="button"
        className="aem-close-btn"
        onClick={onClose}
        disabled={updating}
        aria-label="Close add employee form"
      >
        <i className="bi bi-x-lg" />
      </button>

      <div className="aem-card">
        <div className="aem-top-row">
          <div className="aem-header">
            <div className="aem-photo-section">
              <button
                type="button"
                className="aem-image-wrap"
                onClick={openImagePicker}
                disabled={updating}
                aria-label={
                  image ? "Change employee photo" : "Add employee photo"
                }
              >
                {imagePreviewUrl ? (
                  <img src={imagePreviewUrl} alt="Selected employee preview" />
                ) : (
                  <span className="aem-image-placeholder">
                    <i className="bi bi-person-image" />
                    <span>Add Photo</span>
                  </span>
                )}

                <span className="aem-image-overlay">
                  <i className="bi bi-camera-fill" />
                </span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </div>

            <div className="aem-header-info">
              <span className="aem-title">Add Employee</span>

              <p className="aem-subtitle">
                Create a new staff profile and assign a fleet vehicle
              </p>

              <div className="aem-meta">
                <p>
                  <span>Status</span>

                  <span className="aem-meta-value">New profile</span>
                </p>

                <p>
                  <span>Photo</span>

                  <span className="aem-meta-value">
                    {image ? image.name : "Optional"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          id="add-employee-form"
          className="aem-data-area"
          onSubmit={handleSubmit}
          noValidate
        >
          <FormSection title="Assignment" icon="bi-truck">
            <FormField
              label="Assigned Vehicle"
              htmlFor="aem-vehicle-select"
              hint="Optional"
              wide
            >
              <Select
                inputId="aem-vehicle-select"
                className="aem-select"
                classNamePrefix="aem-select"
                options={vehicleOptions}
                value={selectedVehicleOption}
                onChange={handleVehicleChange}
                isClearable
                isDisabled={updating}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                placeholder="Search registration..."
                noOptionsMessage={() => "No vehicles available"}
                styles={selectStyles}
              />
            </FormField>
          </FormSection>

          <FormSection title="Profile" icon="bi-person">
            <FormField label="Full Name" htmlFor="aem-name" required>
              <input
                id="aem-name"
                type="text"
                name="name"
                placeholder="Enter employee name"
                value={newEmployee.name}
                onChange={handleChange}
                disabled={updating}
                autoComplete="name"
                required
              />
            </FormField>

            <FormField label="Email Address" htmlFor="aem-email" required>
              <input
                id="aem-email"
                type="email"
                name="email"
                placeholder="email@company.com"
                value={newEmployee.email}
                onChange={handleChange}
                disabled={updating}
                autoComplete="email"
                required
              />
            </FormField>
          </FormSection>

          <FormSection title="Security & Contact" icon="bi-shield-lock">
            <FormField label="Phone Number" htmlFor="aem-phone" hint="Optional">
              <input
                id="aem-phone"
                type="tel"
                name="phone"
                placeholder="0400 000 000"
                value={newEmployee.phone}
                onChange={handleChange}
                disabled={updating}
                autoComplete="tel"
              />
            </FormField>

            <FormField
              label="System Passcode"
              htmlFor="aem-passcode"
              hint="Optional"
              sensitive
            >
              <input
                id="aem-passcode"
                type="text"
                name="passcode"
                placeholder="Set login PIN"
                value={newEmployee.passcode}
                onChange={handleChange}
                disabled={updating}
                inputMode="numeric"
                autoComplete="off"
              />
            </FormField>
          </FormSection>
        </form>
      </div>

      <footer className="aem-footer">
        <div className="aem-footer-inner">
          <div className="aem-actions">
            <button
              type="submit"
              form="add-employee-form"
              className="aem-submit-cta"
              disabled={updating}
            >
              {updating ? (
                <>
                  <span className="aem-spinner" aria-hidden="true" />

                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Employee</span>
              )}
            </button>

            <button
              type="button"
              className="aem-cancel-btn"
              onClick={onClose}
              disabled={updating}
            >
              Cancel
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FormSection = ({ title, icon, children }) => (
  <section className="aem-section">
    <header>
      <i className={`bi ${icon}`} />
      <span>{title}</span>
    </header>

    <div className="aem-grid">{children}</div>
  </section>
);

const FormField = ({
  label,
  htmlFor,
  hint,
  required = false,
  wide = false,
  sensitive = false,
  children,
}) => (
  <div
    className={["aem-field", wide ? "wide" : "", sensitive ? "sensitive" : ""]
      .filter(Boolean)
      .join(" ")}
  >
    <label htmlFor={htmlFor}>
      <span>{label}</span>

      {required && (
        <span className="aem-required" aria-hidden="true">
          *
        </span>
      )}

      {hint && <span className="aem-field-hint">{hint}</span>}
    </label>

    <div className="aem-control">{children}</div>
  </div>
);

export default memo(AddEmployeeModal);
